import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import LogRocket from 'logrocket';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ClientRepService } from './../api/services/client-rep.service';
import { AccountInfoVm } from './../models/accountInfo.vm';

@Injectable({
  providedIn: 'root',
})
export class AccountInfoService {
  private accountInfoSubject = new BehaviorSubject<AccountInfoVm | null>(null); // Holds the current account info
  private isLoadedSubject = new BehaviorSubject<boolean>(false); // Tracks if account info is fully loaded

  constructor(
    private authService: MsalService,
    private clientRepService: ClientRepService
  ) {}

  // Expose accountInfo as an Observable
  get accountInfo$(): Observable<AccountInfoVm | null> {
    return this.accountInfoSubject.asObservable();
  }

  // Expose loading state as an Observable
  get isLoaded$(): Observable<boolean> {
    return this.isLoadedSubject.asObservable();
  }

  setAccountInfo(accountInfo: AccountInfo | AccountInfoVm | null): void {
    if (
      this.accountInfoSubject.value === undefined ||
      this.accountInfoSubject.value === null
    ) {
      const createAccountInfoVm = (
        accountInfo: AccountInfo,
        repId: number | null = null
      ): AccountInfoVm => {
        return {
          ...accountInfo,
          repId,
          isLoaded: false, // Initialize as not loaded; will be updated later
          isAdmin:
            accountInfo.idTokenClaims?.roles?.includes('surveys.admin') ??
            false, // Determine if the user is an admin
        };
      };
      if (accountInfo !== null) {
        const accountInfoVm = createAccountInfoVm(accountInfo);
        this.accountInfoSubject.next(accountInfoVm); // Emit the new account info
      }

      this.getRepId().subscribe({
        next: (r) => {
          if (r.body) {
            if (r.body.length === 0) {
              console.error(r.body);
            } else {
              const updatedAccountInfo = {
                ...this.accountInfoSubject.value,
                repId: r.body[0].employeeId ?? null,
                isLoaded: true, // Mark as loaded
                isAdmin:
                  this.accountInfoSubject.value?.idTokenClaims?.roles?.includes(
                    'surveys.admin'
                  ) ?? false, // Determine if the user is an admin
              } as AccountInfoVm;
              this.accountInfoSubject.next(updatedAccountInfo); // Update account info with repId

              // Add user info to LogRocket
              LogRocket.identify(updatedAccountInfo.repId!.toString(), {
                name: updatedAccountInfo.name!,
                email: updatedAccountInfo.username,
                isAdmin: updatedAccountInfo.isAdmin,
              });

              this.isLoadedSubject.next(true); // Mark as fully loaded
            }
          }
        },
        error: (error) => {
          console.error('Error fetching repId:', error);
          this.isLoadedSubject.next(true); // Mark as loaded even if repId fails
        },
      });
    }
  }

  getRepId() {
    return this.clientRepService.getClientReps$Json$Response({
      email: this.accountInfoSubject.value?.username,
    });
  }

  setRepId(repId: number | null) {
    const updatedAccountInfo = {
      ...this.accountInfoSubject.value,
      repId: repId,
    } as AccountInfoVm;

    this.accountInfoSubject.next(updatedAccountInfo); // Update account info with repId
  }

  getAccountInfo(): AccountInfoVm | null {
    console.log(
      'this.accountInfo',
      JSON.stringify(this.accountInfoSubject.value)
    );
    return this.accountInfoSubject.value;
  }

  loadAccountInfo(): Observable<AccountInfo | null> {
    const activeAccount = this.authService.instance.getActiveAccount();
    if (activeAccount) {
      this.setAccountInfo(activeAccount);
      return of(activeAccount);
    } else {
      const accounts = this.authService.instance.getAllAccounts();
      if (accounts.length > 0) {
        const acct = accounts[0];
        this.authService.instance.setActiveAccount(acct);
        this.setAccountInfo(acct);
        return of(acct);
      } else {
        return of(null);
      }
    }
  }

  hasRole(role: string): boolean {
    const roles = this.accountInfoSubject.value?.idTokenClaims?.roles;
    if (roles && roles.includes(role)) {
      return true;
    } else {
      return false;
    }
  }

  hasAnyRole(roles: string[]): boolean {
    return roles.some((role) => this.hasRole(role));
  }
}
